import * as React from 'react';
import { atom, useRecoilState } from 'recoil';

import {
    createPalette,
    type PaletteCommandDef,
    PaletteCommandType,
    usePaletteHandle,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';

export type FilesSortOrder = 'asc' | 'desc';

export type FilesSort = {
    by: 'updated' | 'size';
    order: FilesSortOrder;
};

const filesSortState = atom<FilesSort>({
    key: 'filesSort',
    default: { by: 'updated', order: 'desc' },
});

/**
 * Use the current sort state.
 */
export function useFilesSort() {
    return useRecoilState(filesSortState);
}

/**
 * Button to choose how to sort files.
 */
export function FilesSortButton() {
    const [sort, setSort] = useRecoilState(filesSortState);
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const palette = usePaletteHandle(buttonRef);

    const onPress = () => {
        palette.open(
            createPalette({
                groups: [
                    {
                        key: 'sort_updated',
                        title: 'Modification date',
                        commands: createSortCommands(sort, 'updated', setSort),
                    },
                    {
                        key: 'sort_size',
                        title: 'Size',
                        commands: createSortCommands(sort, 'size', setSort),
                    },
                ],
            })
        );
    };

    return (
        <Button
            ref={buttonRef}
            kind="secondary"
            leadingIcon={getSortOrderIcon(sort.order)}
            label="Sort files"
            onPress={onPress}
            tooltip={{
                label: 'Ordering',
                description: getDescription(sort),
            }}
            iconOnly
        />
    );
}

/**
 * Get a description for a sorting strategy.
 */
function getDescription(sort: FilesSort): string {
    switch (sort.by) {
        case 'updated':
            return sort.order === 'desc' ? 'Recently updated' : 'Least recently updated';
        case 'size':
            return sort.order === 'desc' ? 'Largest files first' : 'Smallest files first';
    }
}

/**
 * Get the icon for a sorting direction.
 */
function getSortOrderIcon(order: FilesSort['order']) {
    return order === 'desc' ? Icon.SortDown : Icon.SortUp;
}

/**
 * Create commands for a sorting property.
 */
function createSortCommands(
    current: FilesSort,
    by: FilesSort['by'],
    onChange: (sort: FilesSort) => void
): PaletteCommandDef[] {
    return (['desc', 'asc'] as const).map((order) => {
        const sort = { by, order };
        return {
            type: PaletteCommandType.RADIO,
            key: `sort_${by}_${order}`,
            title: getDescription(sort),
            icon: getSortOrderIcon(sort.order),
            selected: sort.by === current.by && sort.order === current.order,
            trigger: () => {
                onChange(sort);
            },
        };
    });
}
